import type { ComponentConfig } from "@puckeditor/core";
import { Gallery } from './gallery';
import { GalleryFields } from "./gallery.fields";
import type { GalleryProps } from './gallery';
import { ImagePicker } from "../editor/ImagePicker";
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { ProjectPicker } from "../editor/ProjectPicker";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { generatedSubheading } from "../../lib/generated-subheading";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { metadataIsOnePage } from "../../lib/one-page-metadata";

const GalleryConfig: ComponentConfig<GalleryProps> = {
  defaultProps: {
    columns: "3",
    heading: "See Our Recent Work",
    images: [
      {
        afterLabel: "After",
        alt: "Finished project",
        beforeLabel: "Before",
        caption: "Recent completed project",
        imageUrl: "",
        tag: "Featured Work",
      },
      {
        afterLabel: "After",
        alt: "Project detail",
        beforeLabel: "Before",
        caption: "Craftsmanship close-up",
        imageUrl: "",
        tag: "Detail",
      },
      {
        afterLabel: "After",
        alt: "Before and after result",
        beforeLabel: "Before",
        caption: "Transformation result",
        imageUrl: "",
        tag: "Before & After",
      },
    ],
    layout: "showcase",
    showCaptions: "yes",
    showTags: "yes",
    sliderWiggle: "on",
    subheading:
      "A quick look at finished projects, quality details, and the kind of results our customers see.",
    variant: "cards",
  },
  fields: {
    heading: {
      ...GalleryFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="gallery.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...GalleryFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="gallery.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: GalleryFields.layout,
    variant: GalleryFields.variant,
    columns: GalleryFields.columns,
    showCaptions: GalleryFields.showCaptions,
    showTags: GalleryFields.showTags,
    sliderWiggle: GalleryFields.sliderWiggle,
    images: {
      ...GalleryFields.images,
      arrayFields: {
        ...GalleryFields.images.arrayFields,
        sourceProjectId: {
          ...GalleryFields.images.arrayFields.sourceProjectId,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ProjectPicker value={value || ""} onChange={onChange} label="Synced Project" />,
        },
        imageUrl: {
          ...GalleryFields.images.arrayFields.imageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Cover Image" />,
        },
        photos: {
          ...GalleryFields.images.arrayFields.photos,
          arrayFields: {
            ...GalleryFields.images.arrayFields.photos.arrayFields,
            imageUrl: {
              ...GalleryFields.images.arrayFields.photos.arrayFields.imageUrl,
              render: ({
                value,
                onChange,
              }: {
                value?: string;
                onChange: (val: string | undefined) => void;
              }) => <ImagePicker value={value || ""} onChange={onChange} label="Photo" />,
            },
          },
        },
        beforeImageUrl: {
          ...GalleryFields.images.arrayFields.beforeImageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Before Image" />,
        },
        afterImageUrl: {
          ...GalleryFields.images.arrayFields.afterImageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="After Image" />,
        },
      },
    } as any,
    backgroundColor: GalleryFields.backgroundColor,
  },
  label: "Gallery",
  render: ({ id, puck, ...props }) => {
    const puckMetadata = puck?.metadata as
        | { business?: JobsiteBusinessContext; site?: { id?: string }; apiUrl?: string }
        | undefined,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business,
      market = business?.primaryMarket,

      resolvedProps = {
        ...props,
        columns: props.columns || "3",
        heading:
          props.heading ||
          (market ? `Recent Work in ${market.name}` : "See Our Recent Work"),
        layout: props.layout || "showcase",
        puckApiUrl: puckMetadata?.apiUrl,
        // For synced-project pulls; metadata beats window config in the editor
        // Iframes (window config lands after first paint there).
        puckSiteId: puckMetadata?.site?.id,
        showCaptions: props.showCaptions || "yes",
        showTags: props.showTags || "yes",
        subheading:
          props.subheading ||
          generatedSubheading(
            props.variant,
            market
              ? `Browse real project photos from jobs completed in ${market.name} and nearby service areas.`
              : "Browse real project photos and recent completed work.",
            metadataIsOnePage(puck?.metadata),
          ),
      };

    return <Gallery id={id} {...resolvedProps} />;
  },
  /**
   * A control that changes nothing teaches the pro to stop trusting the panel,
   * so every field is hidden in the layouts where the render ignores it.
   *
   * The slider wiggle only acts on Before & After items, so it shows only when there is one.
   * A scrolling strip sizes its tiles to the screen, so it has no column count.
   */
  resolveFields: (data, { fields }) => {
    const layout = (data.props as { layout?: string })?.layout || "showcase",
      isBeforeAfter = layout === "before-after",
      resolved = { ...fields } as Record<string, any>,
      {images} = resolved;

    const hasComparisons = ((data.props as { images?: { kind?: string }[] }).images ?? []).some((image) => image.kind === "before-after");
    if (!isBeforeAfter && !hasComparisons) {
      delete resolved.sliderWiggle;
    }

    if (isBeforeAfter) {
      // The comparison card ignores the cards/editorial treatment entirely.
      delete resolved.variant;
    } else if (layout === "strip") {
      delete resolved.columns;
    }

    if (!images?.arrayFields) {
      return resolved as typeof fields;
    }

    // The pair fields stay visible in every layout: a project with both
    // Photos set shows a Before/After badge on its tile and opens as an
    // Interactive comparison in the lightbox.
    const hidden = isBeforeAfter
        ? // No lightbox, no click-through, and the cover image, focus, and
          // Project sync only affect tiles, none of which the comparison card
          // Renders.
          ["photos", "clickAction", "href", "imageUrl", "position", "sourceProjectId"]
        : [],

      arrayFields = { ...images.arrayFields };
    for (const key of hidden) {
      delete arrayFields[key];
    }
    if (layout === "masonry") {
      // Masonry images keep their own proportions, so there is no crop for
      // The focus setting to steer.
      delete arrayFields.position;
    }

    resolved.images = { ...images, arrayFields };
    return resolved as typeof fields;
  },
};

export { GalleryConfig };
